import type { UpcomingVisit } from '@/api/types/ProviderApi'
import { Glyph } from '@/components/svgs/Glyph'
import { colors } from '@/constants/colors'
import { mapUrlFor } from '@/features/scheduling/availability'
import { studioTitle } from '@/features/visits/studio'
import { analytics } from '@/lib/analytics'
import { useDiscColor } from '@/ui/core/card-ground'
import { Pressable } from '@/ui/core/pressable'
import { VStack } from '@/ui/core/stack'
import { Text } from '@/ui/core/typography'
import { Linking, View } from 'react-native'

/**
 * The way there, as the foot of the card rather than a card of its own.
 *
 * Where a visit is belongs to the visit. Set beside the appointment as a second panel it read as
 * a second subject; joined to it, the card answers when and where as one fact and the page holds
 * one object instead of two.
 *
 * No map is drawn, for the reason `StudioSheet` gives: an embedded one is either a key the app
 * has to carry and renew or a native map engine to host, and neither buys anything over the map
 * the member already has installed and can navigate from — and a street grid that is not this
 * street, under a pin that is not this door, is a lie told to someone trying to find a building.
 * So this is the shape a map would have been, and pressing it opens the phone's own.
 *
 * It opens on the studio's own coordinates now, through the same `mapUrlFor` the picker uses —
 * Apple's scheme on iOS, Android's `geo:` on Android, the studio's name riding along so the pin
 * is labelled. It used to go through a table of three Google links hardcoded in `VisitCard`,
 * which sent the other two studios to Tribeca's door.
 */
/** "249 Church Street", or "249 Church Street, Suite 3" — the door, not the city it is in. */
const streetLine = (visit: UpcomingVisit) => {
	const parts = visit.structuredAddress
	if (!parts) {
		// The payload that predates provider-kapi#1162 has only the joined line.
		return visit.address
	}
	return [parts.street1, parts.street2].filter(Boolean).join(', ')
}

/**
 * "Inside WeWork at 249 Church Street", or just the street where the studio has its own door.
 *
 * One sentence, in the order a member would say it: the building first, because that is what they
 * are looking for from the pavement, and the number that gets them to it second. It was the two
 * facts either side of a dot, which read as a list of two places rather than one place inside
 * another.
 */
const whereLine = (visit: UpcomingVisit) =>
	visit.siteName ? `Inside ${visit.siteName} at ${streetLine(visit)}` : streetLine(visit)

export const MapWay = ({ visit }: { visit: UpcomingVisit }) => {
	const studio = studioTitle(visit.locationName)
	const disc = useDiscColor()

	return (
		<Pressable
			onPress={() => {
				analytics.trackClick('visit-directions', {
					appointmentId: visit.appointmentId,
					locationId: visit.locationId,
				})
				// The brand's name rides along, so the pin the map drops is labelled the way the
				// row that opened it is — and so a member searching again later finds the studio
				// rather than a street number.
				Linking.openURL(
					mapUrlFor({ name: studio, coordinates: visit.coordinates ?? null })
				)
			}}
			accessibilityRole="button"
			accessibilityLabel={`${studio}, ${visit.address}. Open in Maps`}
			className="flex-row items-center px-4 py-4"
			style={{ gap: 12 }}
		>
			<View
				className="h-10 w-10 items-center justify-center rounded-full"
				style={{ backgroundColor: disc }}
			>
				<Glyph name="pin" size={20} color={colors.ink} />
			</View>
			<VStack gap={2} className="flex-1">
				{/* The studio, then everything about where it is on one line under it. "Inside
			    WeWork" was a line of its own, which made the building a third fact rather than
			    part of the address — a member reads "249 Church Street, inside WeWork" as one
			    instruction, because that is how they would say it out loud. */}
				<Text size="body1" weight="bold">
					{studio}
				</Text>
				<Text size="body2" tone="muted" numberOfLines={2}>
					{whereLine(visit)}
				</Text>
			</VStack>
			{/* An angled arrow, where every other row in this card has a chevron.

			    The distinction is the point: a chevron means the app has something more to show
			    you, and this row hands you to Apple Maps. It is the arrow rather than the chevron
			    turned, which was tried: a chevron is two thick strokes meeting at a right angle,
			    and at forty-five degrees it stops reading as a direction and starts reading as a
			    corner bracket. The arrow has a shaft, which is what survives the rotation. */}
			<Glyph name="arrow" size={14} color={colors.ink} rotate={-45} />
		</Pressable>
	)
}
